import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import { IssuePeekOverview } from '@/components/IssuePeekOverview';
import {
  Plus,
  Calendar,
  ChevronRight,
  Trash2,
  Edit2,
  Clock,
  CheckCircle2,
  Circle,
  Target,
} from 'lucide-react';
import clsx from 'clsx';

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  completed: { label: 'Completed', color: 'text-green-700', bg: 'bg-green-100' },
  current: { label: 'Current', color: 'text-blue-700', bg: 'bg-blue-100' },
  upcoming: { label: 'Upcoming', color: 'text-gray-700', bg: 'bg-gray-100' },
};

export function Cycles() {
  const { projectId } = useParams();
  const api = useApi();
  const queryClient = useQueryClient();

  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingCycle, setEditingCycle] = useState<string | null>(null);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
  });

  const { data: cycles, isLoading } = useQuery({
    queryKey: ['cycles', projectId],
    queryFn: () => api.getCycles(projectId!),
    enabled: !!projectId,
  });

  const { data: workItems } = useQuery({
    queryKey: ['workitems', projectId],
    queryFn: () => api.getWorkItems(projectId!),
    enabled: !!projectId,
  });

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId!),
    enabled: !!projectId,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.createCycle(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycles', projectId] });
      setIsCreating(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateCycle(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycles', projectId] });
      setEditingCycle(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCycle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycles', projectId] });
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
      if (selectedCycleId === editingCycle) setSelectedCycleId(null);
    },
  });

  const resetForm = () => {
    setFormData({ name: '', description: '', startDate: '', endDate: '' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    if (editingCycle) {
      updateMutation.mutate({
        id: editingCycle,
        data: {
          bh_name: formData.name,
          bh_description: formData.description,
          bh_startdate: formData.startDate,
          bh_enddate: formData.endDate,
        },
      });
    } else {
      createMutation.mutate({
        bh_name: formData.name,
        bh_description: formData.description,
        bh_startdate: formData.startDate,
        bh_enddate: formData.endDate,
        bh_project: projectId,
      });
    }
  };

  const startEditing = (cycle: any) => {
    setEditingCycle(cycle.bh_cycleid);
    setFormData({
      name: cycle.bh_name,
      description: cycle.bh_description || '',
      startDate: cycle.bh_startdate || '',
      endDate: cycle.bh_enddate || '',
    });
    setIsCreating(true);
  };

  const getCycleWorkItems = (cycleId: string) => {
    return workItems?.filter((wi: any) => wi.bh_cycle === cycleId) || [];
  };

  const getCycleProgress = (cycleId: string) => {
    const items = getCycleWorkItems(cycleId);
    if (items.length === 0) return { total: 0, completed: 0, percentage: 0 };

    const completed = items.filter((wi: any) => {
      const state = states?.find((s: any) => s.bh_stateid === wi._bh_state_value);
      return state?.bh_group === 4; // Completed group
    }).length;

    return {
      total: items.length,
      completed,
      percentage: Math.round((completed / items.length) * 100),
    };
  };

  const getStateById = (stateId: string) => states?.find((s: any) => s.bh_stateid === stateId);

  const formatDateRange = (start: string, end: string) => {
    if (!start || !end) return 'No dates set';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  };

  const getDaysRemaining = (endDate: string) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const today = new Date();
    const diff = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const selectedCycle = cycles?.find((c: any) => c.bh_cycleid === selectedCycleId);

  return (
    <div className="h-full flex">
      {/* Cycles List */}
      <div className="w-80 border-r border-gray-200 bg-white flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-semibold text-gray-900">Cycles</h1>
            <button
              onClick={() => {
                setIsCreating(true);
                setEditingCycle(null);
                resetForm();
              }}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {isCreating && (
            <form onSubmit={handleSubmit} className="space-y-3 p-3 bg-gray-50 rounded-lg">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Cycle name"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description (optional)"
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">End</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={!formData.name.trim()}
                  className="flex-1 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {editingCycle ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreating(false);
                    setEditingCycle(null);
                    resetForm();
                  }}
                  className="px-3 py-2 text-gray-600 text-sm hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {cycles?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No cycles yet</p>
              <p className="text-sm">Create your first sprint</p>
            </div>
          ) : (
            <div className="space-y-2">
              {cycles?.map((cycle: any) => {
                const progress = getCycleProgress(cycle.bh_cycleid);
                const status = STATUS_CONFIG[cycle.bh_status] || STATUS_CONFIG.upcoming;
                const daysRemaining = getDaysRemaining(cycle.bh_enddate);

                return (
                  <button
                    key={cycle.bh_cycleid}
                    onClick={() => setSelectedCycleId(cycle.bh_cycleid)}
                    className={clsx(
                      'w-full p-3 rounded-lg text-left transition-colors',
                      selectedCycleId === cycle.bh_cycleid
                        ? 'bg-primary-50 border-2 border-primary-500'
                        : 'hover:bg-gray-50 border-2 border-transparent'
                    )}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{cycle.bh_name}</h3>
                        <p className="text-xs text-gray-500">
                          {formatDateRange(cycle.bh_startdate, cycle.bh_enddate)}
                        </p>
                      </div>
                      <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', status.bg, status.color)}>
                        {status.label}
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>{progress.completed}/{progress.total} issues</span>
                        <span>{progress.percentage}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full transition-all"
                          style={{ width: `${progress.percentage}%` }}
                        />
                      </div>
                    </div>

                    {cycle.bh_status === 'current' && daysRemaining !== null && (
                      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Ending today'}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Cycle Detail */}
      <div className="flex-1 bg-gray-50 overflow-y-auto">
        {selectedCycle ? (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{selectedCycle.bh_name}</h2>
                {selectedCycle.bh_description && (
                  <p className="text-gray-600 mt-1">{selectedCycle.bh_description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => startEditing(selectedCycle)}
                  className="p-2 hover:bg-gray-200 rounded-lg text-gray-600"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm('Delete this cycle?')) {
                      deleteMutation.mutate(selectedCycle.bh_cycleid);
                    }
                  }}
                  className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Cycle Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {(() => {
                const progress = getCycleProgress(selectedCycle.bh_cycleid);
                const items = getCycleWorkItems(selectedCycle.bh_cycleid);
                const inProgress = items.filter((wi: any) => {
                  const state = states?.find((s: any) => s.bh_stateid === wi._bh_state_value);
                  return state?.bh_group === 3;
                }).length;

                return (
                  <>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                        <Target className="w-4 h-4" />
                        Total Issues
                      </div>
                      <p className="text-2xl font-semibold text-gray-900">{progress.total}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 text-blue-500 text-sm mb-1">
                        <Circle className="w-4 h-4" />
                        In Progress
                      </div>
                      <p className="text-2xl font-semibold text-gray-900">{inProgress}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 text-green-500 text-sm mb-1">
                        <CheckCircle2 className="w-4 h-4" />
                        Completed
                      </div>
                      <p className="text-2xl font-semibold text-gray-900">{progress.completed}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                        <Clock className="w-4 h-4" />
                        Progress
                      </div>
                      <p className="text-2xl font-semibold text-gray-900">{progress.percentage}%</p>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Issues in Cycle */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="font-medium text-gray-900">Issues in this cycle</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {getCycleWorkItems(selectedCycle.bh_cycleid).length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No issues assigned to this cycle
                  </div>
                ) : (
                  getCycleWorkItems(selectedCycle.bh_cycleid).map((item: any) => {
                    const state = getStateById(item._bh_state_value);
                    return (
                      <button
                        key={item.bh_workitemid}
                        onClick={() => setSelectedIssueId(item.bh_workitemid)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: state?.bh_color || '#6b7280' }}
                        />
                        <span className="text-sm text-gray-500 font-mono">
                          #{item.bh_sequenceid}
                        </span>
                        <span className="flex-1 text-sm text-gray-900 truncate">
                          {item.bh_name}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Select a cycle to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* Issue Peek Overview */}
      {selectedIssueId && projectId && (
        <IssuePeekOverview
          issueId={selectedIssueId}
          projectId={projectId}
          onClose={() => setSelectedIssueId(null)}
        />
      )}
    </div>
  );
}
